import { Navigate } from "react-router-dom";
import { Ear, HeartHandshake, Sprout, ShieldCheck, Globe, Clock, Phone } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { LangSwitch, TauMark } from "../components/app/Layout";

const PILLARS = [
  { icon: Ear, title: "Listen", text: "Understand the real circumstances of our households through consented surveys." },
  { icon: HeartHandshake, title: "Respond", text: "Turn verified findings into pastoral priorities discerned by our leaders." },
  { icon: Sprout, title: "Grow", text: "Monitor and evaluate our mission responsibly, together as one parish." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] paper-grain grid lg:grid-cols-[1.1fr_0.9fr]">
      <section className="flex flex-col px-6 sm:px-10 lg:px-16 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-amber-900">
            <TauMark className="h-8 w-8" />
            <span className="text-xs uppercase tracking-[0.25em] font-semibold">Saint Francis of Assisi Parish</span>
          </div>
          <LangSwitch />
        </div>
        <div className="my-auto py-14 max-w-2xl">
          <p className="rise text-xs uppercase tracking-[0.25em] font-semibold text-amber-800/80">Barangay Mabuhay · General Santos City</p>
          <h1 className="rise mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-amber-950" style={{ animationDelay: "80ms" }} data-testid="landing-title">
            MABUHAY PASTORAL INTELLIGENCE
          </h1>
          <p className="rise mt-5 font-serif-display text-xl sm:text-2xl italic text-amber-900" style={{ animationDelay: "160ms" }} data-testid="landing-subtitle">{t("subtitle")}</p>
          <p className="rise mt-3 text-sm sm:text-base text-stone-600" style={{ animationDelay: "220ms" }}>{t("tagline")}</p>
          <button onClick={login} data-testid="google-signin-button" style={{ animationDelay: "300ms" }}
            className="rise mt-10 inline-flex items-center gap-3 rounded-full bg-amber-900 px-7 py-3.5 text-sm font-semibold text-amber-50 shadow-lg shadow-amber-900/20 hover:bg-[#5B210B] hover:-translate-y-0.5 transition-[background-color,transform]">
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="#FEF3C7" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z" /><path fill="#FDE68A" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z" /><path fill="#FCD34D" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9z" /><path fill="#FFFBEB" d="M12 6c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6z" /></svg>
            {t("signin")}
          </button>
          <p className="mt-4 flex items-center gap-2 text-xs text-stone-500"><ShieldCheck className="h-4 w-4 text-[#285E3B]" /> {t("landing_note")}</p>
          <div className="mt-14 grid sm:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="rise border-t-2 border-amber-800/30 pt-4" style={{ animationDelay: `${380 + i * 80}ms` }}>
                <p.icon className="h-5 w-5 text-[#285E3B]" />
                <p className="mt-2 font-serif-display text-xl font-semibold text-amber-950">{p.title}</p>
                <p className="mt-1 text-sm text-stone-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
        <footer className="space-y-2 border-t border-amber-800/15 pt-4 pb-14 text-xs text-stone-600" data-testid="landing-footer">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <a href="https://mabuhayparish.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-semibold text-amber-900 hover:underline" data-testid="footer-website-link"><Globe className="h-3.5 w-3.5" />mabuhayparish.org</a>
            <span className="flex items-center gap-1.5" data-testid="footer-office-hours"><Clock className="h-3.5 w-3.5 text-[#285E3B]" />Parish office: [office hours]</span>
            <span className="flex items-center gap-1.5" data-testid="footer-contact"><Phone className="h-3.5 w-3.5 text-[#285E3B]" />[contact]</span>
          </div>
          <p className="text-stone-400">Technology in service of pastoral accompaniment — never a replacement for it.</p>
        </footer>
      </section>
      <section className="relative hidden lg:block">
        <img src="/images/hero.jpg" alt="Parish church" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-950/70 via-amber-950/10 to-transparent" />
        <blockquote className="absolute bottom-10 left-10 right-10 font-serif-display text-2xl italic text-amber-50 leading-snug">
          “Start by doing what's necessary; then do what's possible; and suddenly you are doing the impossible.”
          <span className="mt-2 block text-sm not-italic tracking-widest uppercase text-amber-200">Attributed to St. Francis of Assisi</span>
        </blockquote>
      </section>
    </div>
  );
}
